import type React from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { FaceSmile, XClose } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';

export interface CandidateAvailabilityUpcomingPopoverProps {
  isOpen: boolean;
  onDismiss: () => void;
  children?: React.ReactNode;
  /** When true, only render the overlay (no wrapper). Use as a sibling of calendar so toolbar stays clickable. */
  overlayOnly?: boolean;
}

const TITLE = 'More slots available in the following week';
const BODY =
  'Scroll to the next week to see more slots if the ones shown here are not good for you.';

const OverlayContent: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => (
  <Flex align="center" role="group" pointerEvents="none">
    <Flex
      maxW="350px"
      pointerEvents="auto"
      bg="white"
      borderRadius="6px"
      boxShadow="drop-shadows-bottom"
      borderRight="none"
      p="12px"
      border="1px solid"
      borderColor="border-color"
      align="flex-start"
      gap="10px"
    >
      <Flex direction="column" gap="6px" flex={1}>
        <Flex align="center" justify="space-between" gap="6px">
          <FaceSmile
            size={20}
            color="var(--kula-colors-secondary-600)"
            className="kula-icon"
          />
          <KText color="secondary-900" size="sm-medium" whiteSpace="nowrap">
            {TITLE}
          </KText>
          <Icon
            as={XClose}
            boxSize="16px"
            color="secondary-600"
            cursor="pointer"
            onClick={onDismiss}
            _hover={{ color: 'secondary-900' }}
            flexShrink={0}
          />
        </Flex>
        <KText>{BODY}</KText>
      </Flex>
    </Flex>
    <Box position="relative" flexShrink={0} ml="-1px">
      <Box
        width="0"
        height="0"
        borderTop="8px solid transparent"
        borderBottom="8px solid transparent"
        borderLeft="8px solid"
        borderLeftColor="border-color"
      />
      <Box
        position="absolute"
        top="1px"
        left="0"
        width="0"
        height="0"
        borderTop="7px solid transparent"
        borderBottom="7px solid transparent"
        borderLeft="7px solid"
        borderLeftColor="white"
        filter="drop-shadow(2px 0 2px rgba(0, 0, 0, 0.06))"
      />
    </Box>
  </Flex>
);

export const CandidateAvailabilityUpcomingPopover: React.FC<
  CandidateAvailabilityUpcomingPopoverProps
> = ({ isOpen, onDismiss, children, overlayOnly = false }) => {
  if (overlayOnly) {
    return isOpen ? (
      <Box
        position="absolute"
        top="50%"
        right="2px"
        transform="translateY(-50%)"
        zIndex={10}
        pointerEvents="none"
      >
        <Flex justify="flex-end" pointerEvents="none">
          <Box pointerEvents="auto">
            <OverlayContent onDismiss={onDismiss} />
          </Box>
        </Flex>
      </Box>
    ) : null;
  }

  return (
    <Box position="relative" width="100%" height="100%">
      {children}
      {isOpen && (
        <Box
          position="absolute"
          top="50%"
          right="2px"
          transform="translateY(-50%)"
          zIndex={10}
          pointerEvents="none"
        >
          <Flex justify="flex-end" pointerEvents="none">
            <Box pointerEvents="auto">
              <OverlayContent onDismiss={onDismiss} />
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
};
